import { Building2, Wallet, ShieldCheck, CheckCircle2, Clock, Percent } from "lucide-react";
import tryggaLogo from "@/assets/trygga_stor_compressed.webp";
import ucLogo from "@/assets/UC.png";
import bankTransferIcon from "@/assets/banköverföring.png";
import creditsafeImg from "@/assets/creditsafe.png";
import { ProviderData } from "@/types/provider";

export const tryggaData: ProviderData = {
  // Basic Info
  id: "trygga",
  name: "Trygga",
  slug: "trygga",
  logo: tryggaLogo,
  logoSize: "small",
  type: "comparison",
  numberOfLenders: 32,
  
  // Trustpilot
  trustpilot: {
    rating: 4.6,
    reviews: 2375,
    url: "https://se.trustpilot.com/review/trygga.com"
  },
  
  // Hero Section
  hero: {
    badges: [
      { text: "Jämför 32 långivare med en ansökan", variant: "premium" }
    ],
    description: "Trygga Group Sverige AB grundades 2013 under namnet Likvidum. Två år senare fick de tillstånd för att bedriva förmedling av konsumentkrediter i Sverige. Låneförmedlaren har sedan dess \"jämfört över 2 miljoner erbjudanden\" och via detta hjälpt privatpersoner att hitta bättre lånevillkor.",
    ctaButtonText: "Ansök hos Trygga",
    ctaButtonVariant: "default"
  },
  
  // Snabbfakta (Quick Facts) - 6 kort
  stats: [
    {
      icon: Building2,
      value: "32 långivare",
      label: "Jämför erbjudanden",
      description: "Med en ansökan"
    },
    {
      icon: Wallet,
      value: "800 000 kr",
      label: "Maxbelopp",
      description: "från 5000 kr"
    },
    {
      icon: Percent,
      value: "5,07 – 33,62 %",
      label: "Effektiv ränta",
      description: "Varierar"
    },
    {
      icon: Clock,
      value: "1 – 20 år",
      label: "Löptid",
      description: "Flexibel återbetalningstid"
    },
    {
      icon: ShieldCheck,
      value: "UC",
      label: "Kreditupplysning",
      description: "Påverkar UC-Score"
    },
    {
      icon: CheckCircle2,
      value: "Ja",
      label: "Betalningsanmärkningar",
      description: "Hos vissa långivare"
    }
  ],
  
  // Features (Pros & Cons)
  features: {
    pros: [
      "Högt betyg på Trustpilot",
      "Bara en enda kreditupplysning"
    ],
    cons: [
      "Kundtjänst har inte öppet på helger",
      "Färre samarbetande långivare än Lendo och Sambla"
    ]
  },
  
  // Key Information Grid - 4 kort
  keyInformation: [
    {
      image: ucLogo,
      title: "Kreditupplysning",
      value: "UC"
    },
    {
      image: bankTransferIcon,
      title: "Utbetalning via",
      value: "Banköverföring"
    },
    {
      icon: CheckCircle2,
      title: "Effektiv ränta",
      value: "5,07 – 33,62 %",
      description: "Varierar"
    },
    {
      image: creditsafeImg,
      title: "Medlemskap",
      value: "Creditsafe"
    }
  ],
  
  // Terms and Conditions
  termsAndConditions: [
    { term: "Lånebelopp", value: "5 000 - 800 000 kr" },
    { term: "Nominell ränta", value: "4,95 – 21,95 %" },
    { term: "Effektiv ränta", value: "5,07 – 33,62 %" },
    { term: "Löptid", value: "1 – 20 år" },
    { term: "Uppläggningsavgift", value: "(0–995 kr) Varierar mellan långivare" },
    { term: "Fakturaavgift (Pappersfaktura)", value: "Varierar mellan långivare" },
    { term: "Fakturaavgift (Autogiro)", value: "Varierar mellan långivare" },
    { 
      term: "Lån utan UC", 
      value: "Nej",
      badge: { text: "Nej", variant: "default" }
    },
    { term: "Svar på ansökan", value: "Samma dag" },
    { term: "Betalningslösning", value: "Banköverföring" }
  ],
  
  // Loan Requirements
  loanRequirements: [
    { 
      requirement: "Legitimering", 
      value: "Nej",
      badge: { text: "Nej", variant: "success" }
    },
    { 
      requirement: "Folkbokförd i Sverige", 
      value: "Ja, minst 1 år",
      badge: { text: "Ja, minst 1 år", variant: "info" }
    },
    { 
      requirement: "Svenskt personnummer", 
      value: "Ja",
      badge: { text: "Ja", variant: "info" }
    },
    { 
      requirement: "Svenskt mobilnummer", 
      value: "Ja",
      badge: { text: "Ja", variant: "info" }
    },
    { 
      requirement: "Svenskt kontonummer", 
      value: "Ja",
      badge: { text: "Ja", variant: "info" }
    },
    { requirement: "Minsta årsinkomst", value: "120 000 kr" },
    { 
      requirement: "Betalningsanmärkning OK?", 
      value: "Ja",
      badge: { text: "Ja", variant: "success" }
    },
    { requirement: "Ålderskrav", value: "18 +" }
  ],
  
  // Interest Rate Section
  interestRateSection: {
    title: "Tryggas ränta",
    description: "Att ansöka om lån via Trygga är kostnadsfritt. Räntan och månadskostnaden på ditt lån bestäms inte av Trygga utan av deras samarbetspartners. När du gjort en låneansökan via Trygga bedömer långivarna din kreditvärdighet utifrån den kreditupplysning som görs hos UC, och lånevillkoren blir sedan individuellt anpassade.",
    rateTable: [
      { label: "Lånebelopp (lägsta – högsta)", value: "5 000 – 800 000 kr" },
      { label: "Nominell ränta", value: "4,95 – 21,95 %" },
      { label: "Effektiv ränta", value: "5,07 – 33,62 %" },
      { label: "Uppläggningsavgift", value: "Varierar mellan långivare" },
      { label: "Aviavgift", value: "Varierar mellan långivare" },
      { label: "Förtidsinlösen", value: "Kostnadsfri" }
    ]
  },
  
  // Payout Section
  payoutSection: {
    title: "Tryggas utbetalning",
    description: "När du ansöker om lån via Trygga varierar återbetalningstiden beroende på vilken långivare du väljer och hur du signerar avtalet. Men räkna med att det tar cirka 2–5 arbetsdagar för pengarna att komma in på ditt konto.",
    payoutTable: [
      { timeframe: "Beviljad före 12:00", duration: "1-4 dagar" },
      { timeframe: "Beviljad efter 12:00", duration: "1-4 dagar" }
    ],
    disclaimer: "Observera: Utbetalningar sker endast under bankdagar. Om du ansöker eller får godkännande på en helg eller helgdag kan det dröja längre innan pengarna finns tillgängliga på ditt konto."
  },
  
  // FAQ - 6 frågor
  faq: [
    {
      question: "Vilka typer av lån kan tecknas?",
      answer: "Trygga jämför enbart lån utan säkerhet. Det innebär exempelvis privatlån, samlingslån samt billån."
    },
    {
      question: "Kan jag låna pengar hos Trygga trots att jag är sjukskriven?",
      answer: "Ja, sjukpenning räknas som inkomst. Om du har en sjukpenning på minst 10 000 kr/månad före skatt kan du ansöka om lån. Långivarna gör en individuell bedömning baserat på din ekonomiska situation."
    },
    {
      question: "Varför får jag flera brev från UC?",
      answer: "När du ansöker om lån via oss görs endast en kreditupplysning hos UC. Varje långivare som behandlar din ansökan får tillgång till denna upplysning och skickar en omfrågandekopia till dig. Omfrågandekopiorna visar att långivarna tagit del av din information för att göra en riskbedömning – det betyder inte att flera kreditupplysningar har gjorts."
    },
    {
      question: "Kan jag låna pengar fast jag är pensionär?",
      answer: "Ja, pension räknas som inkomst, och om den är minst 10 000 kr/månad före skatt kan du ansöka om lån. Vissa långivare har en åldersgräns på 65 år, men vi samarbetar med aktörer som erbjuder lån även till pensionärer. Om du både arbetar och tar ut pension kan du ange din totala inkomst när du ansöker. Långivarna gör en individuell bedömning baserad på din livssituation."
    },
    {
      question: "Kan Trygga användas för att omförhandla lån?",
      answer: "Ja, tjänsten kan användas för att se räntor och villkor som långivare erbjuder. Är det bättre än befintligt lån kan omförhandling eller flytt av lånet ske."
    },
    {
      question: "Kostar det något att använda Trygga?",
      answer: "Nej, för dig som ansöker om ett lån är det gratis att använda Trygga."
    }
  ],
  
  // Customer Service
  customerService: {
    description: "Du kan kontakta Tryggas kundtjänst via telefon eller mejl. De garanterar svar på frågor inom 48 timmar. Innan du kontaktar Trygga kan du se om du hittar svaret på din fråga på deras supportsida.",
    phone: "010 153 58 58",
    phoneHours: ["Vardagar: 9.00 – 17.30", "Lördag: Stängt", "Söndag: Stängt"],
    email: "kundtjanst@trygga.com",
    emailHours: ["Vardagar: 9.00 – 17.30", "Lördag: Stängt", "Söndag: Stängt"]
  },
  
  // Company Info
  companyInfo: {
    name: "Trygga Group Sverige AB",
    orgNumber: "556949-5772",
    address: "Arenavägen 27",
    city: "121 77 Johanneshov",
    website: "https://www.trygga.com",
    fiRegisterUrl: "https://www.fi.se/sv/vara-register/foretagsregistret/details?id=133948"
  },
  
  // Affiliate Link
  affiliateLink: {
    linkId: "trygga-affiliate-link-id",
    trackingSource: "trygga_page",
    url: "https://trygga.com/lana-pengar/"
  },
  
  // SEO
  seo: {
    title: "Trygga Recension 2025 | Jämför 32 långivare - Villkor & Räntor",
    description: "Läs vår recension av Trygga. Jämför lån från 32 långivare med en ansökan. Låna 10 000 - 800 000 kr. Effektiv ränta 5,07-33,62%. Endast 1 UC-upplysning.",
    keywords: ["trygga", "privatlån", "jämför lån", "låneförmedlare", "32 långivare"]
  },
  
  // Schema.org data
  schema: {
    productName: "Trygga Privatlån - Jämför 32 långivare",
    description: "Jämför privatlån från 32 olika långivare med en ansökan. Belopp 10 000 - 800 000 kr. Svar samma dag.",
    url: "https://klarfinans.se/sv-SE/privatlan/trygga",
    provider: "Trygga",
    minAmount: 10000,
    maxAmount: 800000,
    featureList: [
      "Jämför lån från 32 olika långivare",
      "Gratis att använda",
      "Svar på ansökan samma dag",
      "Endast en kreditupplysning",
      "Flexibla återbetalningsvillkor 1-20 år"
    ]
  }
};
